import { graphAuthHeader } from '@/auth/graph'
import { requestDownload, requestJSON, requestUpload } from '@/api/client'
import type {
    SubmissionComment,
    AIView, Answers, AuditEvent, FileAccess, FileRecord, Form, FormDefinition, FormVersion, Group, InboxItem, RoutingRule,
    Submission, SubmissionDetail, SubmissionStep, Workflow, WorkflowDefinition,
} from './types'

const q = encodeURIComponent

export interface SubmissionList {
    items: Submission[]
    total: number
    page: number
    pageSize: number
}

export const platformApi = {
    // ---- forms ----
    listForms: (all = false) =>
        requestJSON<{ items: Form[] }>('GET', `/api/forms${all ? '?all=1' : ''}`).then((r) => r.items),
    getForm: (idOrSlug: string) =>
        requestJSON<{ form: Form; version: FormVersion | null }>('GET', `/api/forms/${q(idOrSlug)}`),
    createForm: (f: { slug: string; name: string; category?: string; description?: string; number_prefix?: string; icon?: string }) =>
        requestJSON<Form>('POST', '/api/forms', f),
    updateForm: (id: string, f: Partial<Pick<Form, 'name' | 'category' | 'description' | 'icon' | 'status'>>) =>
        requestJSON<Form>('PATCH', `/api/forms/${q(id)}`, f),
    listVersions: (id: string) =>
        requestJSON<{ items: FormVersion[] }>('GET', `/api/forms/${q(id)}/versions`).then((r) => r.items),
    getVersion: (id: string, version: string) =>
        requestJSON<FormVersion>('GET', `/api/forms/${q(id)}/versions/${q(version)}`),
    getDraft: (id: string) =>
        requestJSON<{ form: Form; definition: FormDefinition; base: string }>('GET', `/api/forms/${q(id)}/draft`),
    saveDraft: (id: string, definition: FormDefinition) =>
        requestJSON<{ version: string; problems: string[] }>('PUT', `/api/forms/${q(id)}/draft`, { definition }),
    validateDefinition: (id: string, definition: FormDefinition) =>
        requestJSON<{ problems: string[] }>('POST', `/api/forms/${q(id)}/draft/validate`, { definition }),
    discardDraft: (id: string) => requestJSON<void>('DELETE', `/api/forms/${q(id)}/draft`),
    publish: (id: string, changelog: string) =>
        requestJSON<FormVersion>('POST', `/api/forms/${q(id)}/publish`, { changelog }),

    // ---- submissions ----
    createSubmission: (form: string, title = '', answers: Answers = {}) =>
        requestJSON<Submission>('POST', '/api/submissions', { form, title, answers }),
    saveSubmission: (id: string, body: { title: string; answers: Answers; current_page: number }) =>
        requestJSON<Submission>('PATCH', `/api/submissions/${q(id)}`, body),
    deleteSubmission: (id: string) => requestJSON<void>('DELETE', `/api/submissions/${q(id)}`),
    getSubmission: (id: string) => requestJSON<SubmissionDetail>('GET', `/api/submissions/${q(id)}`),
    submit: (id: string) => requestJSON<Submission>('POST', `/api/submissions/${q(id)}/submit`, undefined, graphAuthHeader),
    reviewEdit: (id: string, changes: Record<string, unknown>, reason: string) =>
        requestJSON<Submission>('PATCH', `/api/submissions/${q(id)}/answers`, { changes, reason }),
    listSubmissions: (p: { scope?: 'mine' | 'all'; form?: string; status?: string; page?: number; pageSize?: number }) => {
        const qs = new URLSearchParams()
        Object.entries(p).forEach(([k, v]) => v !== undefined && v !== '' && qs.set(k, String(v)))
        return requestJSON<SubmissionList>('GET', `/api/submissions?${qs}`)
    },
    submissionAudit: (id: string) =>
        requestJSON<{ items: AuditEvent[] }>('GET', `/api/submissions/${q(id)}/audit`).then((r) => r.items),
    decide: (id: string, step: number, decision: string, comment: string) =>
        requestJSON<{ submission: Submission; step_status: string; finished: boolean; next_step: number }>(
            'POST', `/api/submissions/${q(id)}/steps/${step}/decision`, { decision, comment }),
    sendBack: (id: string, step: number, comment: string) =>
        requestJSON<Submission>('POST', `/api/submissions/${q(id)}/steps/${step}/send-back`, { comment }),
    resubmit: (id: string) => requestJSON<Submission>('POST', `/api/submissions/${q(id)}/resubmit`),
    respondConditions: (id: string, step: number, response: 'accept' | 'decline', comment: string) =>
        requestJSON<{ submission: Submission }>('POST', `/api/submissions/${q(id)}/steps/${step}/conditions`, { response, comment }),
    addComment: (id: string, body: string, internal: boolean) =>
        requestJSON<SubmissionComment>('POST', `/api/submissions/${q(id)}/comments`, { body, internal }),
    revoke: (id: string, comment: string) =>
        requestJSON<Submission>('POST', `/api/submissions/${q(id)}/revoke`, { comment }),
    changeWorkflow: (id: string, workflow_id: string, reason: string) =>
        requestJSON<Submission>('POST', `/api/submissions/${q(id)}/workflow`, { workflow_id, reason }),
    aiSummary: (id: string) => requestJSON<AIView>('GET', `/api/submissions/${q(id)}/ai-summary`),
    regenerateAISummary: (id: string) => requestJSON<AIView>('POST', `/api/submissions/${q(id)}/ai-summary`),
    // ---- files ----
    listFiles: (id: string) =>
        requestJSON<{ items: FileRecord[]; access: FileAccess }>('GET', `/api/submissions/${q(id)}/files`),
    uploadFile: (id: string, field: string, file: File) => {
        const fd = new FormData()
        fd.append('file', file)
        return requestUpload<FileRecord>(`/api/submissions/${q(id)}/files?field=${q(field)}`, fd)
    },
    deleteFile: (id: string, fileId: string) =>
        requestJSON<void>('DELETE', `/api/submissions/${q(id)}/files/${q(fileId)}`),
    /** `reason` is only sent for an administrator override. */
    downloadFile: (id: string, fileId: string, reason?: string) =>
        requestDownload(`/api/submissions/${q(id)}/files/${q(fileId)}${reason ? `?reason=${q(reason)}` : ''}`),
    inbox: () => requestJSON<{ items: InboxItem[] }>('GET', '/api/inbox').then((r) => r.items),

    // ---- groups / workflows / routing ----
    listGroups: () => requestJSON<{ items: Group[] }>('GET', '/api/groups').then((r) => r.items),
    getGroup: (id: string) => requestJSON<Group>('GET', `/api/groups/${q(id)}`),
    createGroup: (name: string, description = '', member_ids: string[] = []) =>
        requestJSON<Group>('POST', '/api/groups', { name, description, member_ids }),
    setGroupMembers: (id: string, member_ids: string[]) =>
        requestJSON<Group>('PUT', `/api/groups/${q(id)}/members`, { member_ids }),
    deleteGroup: (id: string) => requestJSON<void>('DELETE', `/api/groups/${q(id)}`),
    listWorkflows: () => requestJSON<{ items: Workflow[] }>('GET', '/api/workflows').then((r) => r.items),
    getWorkflow: (id: string) => requestJSON<Workflow>('GET', `/api/workflows/${q(id)}`),
    createWorkflow: (w: { name: string; definition: WorkflowDefinition; is_default?: boolean }) =>
        requestJSON<Workflow>('POST', '/api/workflows', w),
    updateWorkflow: (id: string, w: { name: string; definition: WorkflowDefinition; is_default?: boolean; archived?: boolean }) =>
        requestJSON<Workflow>('PUT', `/api/workflows/${q(id)}`, w),
    getRouting: () => requestJSON<{ items: RoutingRule[] }>('GET', '/api/routing').then((r) => r.items),
    putRouting: (items: RoutingRule[]) =>
        requestJSON<{ items: RoutingRule[] }>('PUT', '/api/routing', { items }).then((r) => r.items),
    testRouting: (form: string, answers: Answers, submitter_id: string) =>
        requestJSON<{
            workflow_id?: string
            workflow_name?: string
            steps?: (Omit<SubmissionStep, 'step' | 'round' | 'assignees'> & { order: number; assignees: { id: string; name: string; email: string; via: string }[] })[]
            error?: string
        }>('POST', '/api/routing/test', { form, answers, submitter_id }),
}
